// The encounter controller (11 §4.3, §6.3, §6.4, §9, §11.2, §11.3). Non-React: it owns the
// encounter subscription group, loads each new `encounter.snapshot`/`declaration` into the wasm
// bridge, feeds the AuthoringStore, turns `resolution_log` rows into playback, and runs the
// parity checks. Server rows stay in the SDK cache (C8); this store holds client-only state:
// handles, the board the scene currently shows, control, parity banners, pings.
import { create } from 'zustand';
import type { Identity } from 'spacetimedb';
import { tables, type DbConnection, type EventContext, type SubscriptionHandle } from '../../bindings';
import { getConn, identityHex, onConnected, tsToMs } from '../net/connection';
import { useToasts } from '../net/reducers';
import { bytesToHex, type HeroMeta } from '../sim/adapt';
import { CLASS_NAMES } from '../sim/adapt';
import { COMPANION_KINDS, companionIdOf, isCompanionPick } from '../sim/companion';
import type { WasmSimBridge } from '../sim/WasmSimBridge';
import type { SnapshotHandle } from '../sim/SimBridge';
import type { Intent, ResolutionLog, Snapshot, Tile } from '../sim/types';
import { resendUnsent, useAuthoring } from '../stores/authoring';
import { useLink } from '../stores/link';
import { usePlayback } from '../stores/playback';
import { useSession } from '../stores/session';
import { seasonKey } from '../story/calendar';
import { decodeLook, lookKey } from './look';
import { deviceNow } from '../time';
import { preloadEncounterSfx, preloadSfx } from '../audio';
import { playAfterPlayback } from '../audio/cues';
import { wildFeed } from '../wildlife/feed';
import { countLog, resetStreak, streakBoard, STREAK_SFX } from './streak';
import { worldColours } from '../world/colours';

export interface ServerIntent { rowId: bigint; heroId: number; slot: number; pulseIndex: number; snapshotHash: string; revision: number; committed: boolean; substituted: boolean; owner: string; intent: Intent }
export interface Ping { id: number; owner: string; tile: Tile; kind: number; at: number }
export interface ParityIssue { kind: 'plan' | 'resolve'; pulse: number; expected: string; got: string }

export interface EncounterStore {
  encounterId?: bigint; partyId?: bigint; worldId?: bigint;
  me: string;
  handle?: SnapshotHandle;          // authoring snapshot (latest from the server)
  snapshot?: Snapshot;              // TS view of `handle`
  boardSnapshot?: Snapshot;         // what the scene draws; lags `snapshot` while a log plays (11 §9.2)
  snapshotHashBytes?: Uint8Array;   // encounter.snapshot_hash, for commit_hero
  pulseIndex: number;
  state: number; outcome?: number;
  owners: Record<number, string>;   // hero id → controller identity hex
  /** docs/184: each World member's colour (0-based), as the lobby shows it. */
  colours: Map<string, number>;
  controlled: number[];             // hero ids I control (hero.owner or control_grant.grantee)
  heroNames: Record<number, string>;
  parity: ParityIssue[];
  lastPlanHash?: { pulse: number; client: string; server: string };
  pings: Ping[];
  lockedAt?: number;                // device ms when the current pulse locked (HUD "Locking…")
  /** docs/56 EP3: a resolution is still being played out (queued, playing or settling); the fight's end waits for it. */
  busy: boolean;
  dismissParity(): void;
}

export const useEncounter = create<EncounterStore>()((set) => ({
  me: '', pulseIndex: 0, state: 0, owners: {}, colours: new Map(), controlled: [], heroNames: {}, parity: [], pings: [], busy: false,
  dismissParity() { set({ parity: [] }); },
}));

const DEV_PARITY = import.meta.env.DEV || (typeof __FK_E2E__ !== 'undefined' && __FK_E2E__);

let bridge: WasmSimBridge | undefined;
let sub: SubscriptionHandle | undefined;
let unsubs: Array<() => void> = [];
let current: { encounterId: bigint; partyId: bigint; worldId: bigint } | undefined;
let loadedHash = '';
let prevHandle: SnapshotHandle | undefined;
let currentIntents: ServerIntent[] = [];
let intentsBeforeTx: ServerIntent[] = [];
let txSeen: number | string | undefined;
let flushQueued = false;
let lockedSeen = new Set<number>();
let pendingLogs: Array<{ row: { pulseIndex: number; log: Uint8Array; ended?: number | undefined } }> = [];
let logQueue: ResolutionLog[] = [];
let waitingForPlayback = false;
let pingSeq = 1;
const decodeCache = new Map<string, Intent>();
/** docs/54 RC4: the link dropped since the last flush; the next replay applied is a catch-up. */
let catchingUp = false;
let replayApplied = false;
useLink.subscribe((l) => { if (l.state !== 'online') { catchingUp = true; replayApplied = false; } });

export function installBridge(b: WasmSimBridge): void { bridge = b; }
export function getBridge(): WasmSimBridge | undefined { return bridge; }

const u64 = (v: bigint | number): number => Number(v);

// ---- enter / leave ----------------------------------------------------------------------------

export function enterEncounter(encounterId: bigint, partyId: bigint, worldId: bigint): void {
  if (current && current.encounterId === encounterId) return;
  leaveEncounter();
  current = { encounterId, partyId, worldId };
  preloadEncounterSfx();   // docs/34: the fight's banks fetched before the first swing
  resetStreak();           // docs/101: a streak lives for one fight
  if (killStreaksOn()) void preloadSfx(STREAK_SFX, true);
  useEncounter.setState({ encounterId, partyId, worldId, me: useSession.getState().identity ?? '', parity: [], pings: [], pulseIndex: 0, state: 0, outcome: undefined, lockedAt: undefined, handle: undefined, snapshot: undefined, boardSnapshot: undefined, busy: false });
  unsubs.push(onConnected((c) => wire(c, encounterId, partyId, worldId)));
}

export function leaveEncounter(): void {
  for (const u of unsubs) u();
  unsubs = [];
  sub?.unsubscribe();
  sub = undefined;
  current = undefined;
  loadedHash = '';
  if (bridge) { const h = useEncounter.getState().handle; if (h) bridge.free(h); if (prevHandle) bridge.free(prevHandle); }
  prevHandle = undefined;
  currentIntents = []; intentsBeforeTx = []; lockedSeen = new Set(); pendingLogs = []; logQueue = []; waitingForPlayback = false;
  decodeCache.clear();
  wildFeed.clear();
  useAuthoring.getState().detach();
  useEncounter.setState({ encounterId: undefined, handle: undefined, snapshot: undefined, boardSnapshot: undefined, controlled: [], owners: {}, colours: new Map(), busy: false });
}

function wire(c: DbConnection, encounterId: bigint, partyId: bigint, worldId: bigint): void {
  sub?.unsubscribe();
  // 11 §11.2 encounter group (+ the party rows the roster needs). One subscription set, swapped per encounter.
  sub = c.subscriptionBuilder()
    .onApplied(() => { if (catchingUp) replayApplied = true; queueFlush(); })
    .onError((_ctx, err?: Error) => { console.error('[encounter] subscription error', err); useToasts.getState().push(`encounter subscription failed: ${err?.message ?? err}`, 'error'); })
    .subscribe([
      tables.encounter.where((r) => r.id.eq(encounterId)),
      tables.pulse.where((r) => r.encounterId.eq(encounterId)),
      tables.intent.where((r) => r.encounterId.eq(encounterId)),
      tables.heroRoute.where((r) => r.encounterId.eq(encounterId)),
      tables.pauseState.where((r) => r.encounterId.eq(encounterId)),
      tables.readyState.where((r) => r.encounterId.eq(encounterId)),
      tables.resolutionLog.where((r) => r.encounterId.eq(encounterId)),
      tables.boardPing.where((r) => r.encounterId.eq(encounterId)),
      tables.encounterResult.where((r) => r.encounterId.eq(encounterId)),
      tables.encounterStage.where((r) => r.encounterId.eq(encounterId)),
      tables.partyMember.where((r) => r.partyId.eq(partyId)),
      tables.partyMessage.where((r) => r.partyId.eq(partyId)),
      tables.party.where((r) => r.id.eq(partyId)),
      tables.hero.where((r) => r.worldId.eq(worldId)),
      tables.heroCalling.where((r) => r.worldId.eq(worldId)),
      tables.controlGrant.where((r) => r.worldId.eq(worldId)),
      tables.worldMember.where((r) => r.worldId.eq(worldId)),
      tables.world.where((r) => r.id.eq(worldId)),
      tables.playerColour.where((r) => r.worldId.eq(worldId)),
    ]);
  const onTx = (ctx: EventContext) => {
    const id = (ctx.event as { id?: number | string } | undefined)?.id;
    if (id !== txSeen) { txSeen = id; intentsBeforeTx = currentIntents; }
    queueFlush();
  };
  const t = c.db;
  type Cb<R> = (ctx: EventContext, row: R) => void;
  type UpdCb<R> = (ctx: EventContext, oldRow: R, newRow: R) => void;
  interface Watchable<R> {
    onInsert(cb: Cb<R>): void; onDelete(cb: Cb<R>): void; onUpdate?(cb: UpdCb<R>): void;
    removeOnInsert(cb: Cb<R>): void; removeOnDelete(cb: Cb<R>): void; removeOnUpdate?(cb: UpdCb<R>): void;
  }
  const reg = <R>(table: Watchable<R>, filter: (r: R) => boolean) => {
    const ins: Cb<R> = (ctx, row) => { if (filter(row)) onTx(ctx); };
    const upd: UpdCb<R> = (ctx, _o, n) => { if (filter(n)) onTx(ctx); };
    const del: Cb<R> = (ctx, row) => { if (filter(row)) onTx(ctx); };
    table.onInsert(ins); table.onUpdate?.(upd); table.onDelete(del);
    unsubs.push(() => { table.removeOnInsert(ins); table.removeOnUpdate?.(upd); table.removeOnDelete(del); });
  };
  reg(t.encounter as unknown as Watchable<{ id: bigint }>, (r) => r.id === encounterId);
  reg(t.pulse as unknown as Watchable<{ encounterId: bigint }>, (r) => r.encounterId === encounterId);
  reg(t.encounterStage as unknown as Watchable<{ encounterId: bigint }>, (r) => r.encounterId === encounterId);
  reg(t.intent as unknown as Watchable<{ encounterId: bigint }>, (r) => r.encounterId === encounterId);
  reg(t.hero as unknown as Watchable<{ worldId: bigint }>, (r) => r.worldId === worldId);
  reg(t.controlGrant as unknown as Watchable<{ worldId: bigint }>, (r) => r.worldId === worldId);
  reg(t.partyMember as unknown as Watchable<{ partyId: bigint }>, (r) => r.partyId === partyId);
  reg(t.worldMember as unknown as Watchable<{ worldId: bigint }>, (r) => r.worldId === worldId);
  reg(t.playerColour as unknown as Watchable<{ worldId: bigint }>, (r) => r.worldId === worldId);
  const onLog: Cb<{ encounterId: bigint; pulseIndex: number; log: Uint8Array; ended: number | undefined }> = (ctx, row) => {
    if (row.encounterId !== encounterId) return;
    pendingLogs.push({ row });
    // set at once, inside the transaction: the route sees the fight end only after this is played
    if (usePlayback.getState().hasPlayer && !useEncounter.getState().busy) useEncounter.setState({ busy: true });
    onTx(ctx);
  };
  t.resolutionLog.onInsert(onLog);
  unsubs.push(() => t.resolutionLog.removeOnInsert(onLog));
  const onPing: Cb<{ encounterId: bigint; owner: Identity; x: number; y: number; kind: number }> = (_ctx, row) => {
    if (row.encounterId !== encounterId) return;
    const ping: Ping = { id: pingSeq++, owner: identityHex(row.owner), tile: { x: row.x, y: row.y }, kind: row.kind, at: deviceNow() };
    useEncounter.setState((s) => ({ pings: [...s.pings.filter((p) => p.at > deviceNow() - 4000), ping] }));
  };
  t.boardPing.onInsert(onPing);
  unsubs.push(() => t.boardPing.removeOnInsert(onPing));
  // playback → board catch-up
  let wasPlaying = usePlayback.getState().playing;
  unsubs.push(usePlayback.subscribe((p) => {
    if (wasPlaying && !p.playing) { waitingForPlayback = false; afterPlayback(); }
    wasPlaying = p.playing;
  }));
  queueFlush();
}

function queueFlush(): void {
  if (flushQueued) return;
  flushQueued = true;
  queueMicrotask(() => { flushQueued = false; try { flush(); } catch (err) { console.error('[encounter] flush failed', err); } });
}

// ---- the per-transaction flush ------------------------------------------------------------------

function flush(): void {
  const c = getConn();
  if (!c || !current || !bridge) return;
  const { encounterId, partyId, worldId } = current;
  const enc = [...c.db.encounter.iter()].find((r) => r.id === encounterId);
  if (!enc) return;
  const me = useSession.getState().identity ?? '';

  // 1. control: hero rows of the party, grants, owners
  const members = [...c.db.partyMember.iter()].filter((m) => m.partyId === partyId);
  const grants = new Map<number, string>();
  for (const g of c.db.controlGrant.iter()) if (g.worldId === worldId) grants.set(u64(g.heroId), identityHex(g.grantee));
  const heroes = new Map<number, { owner: string; shapeMark: number; name: string; classId: string; controller: string }>();
  for (const h of c.db.hero.iter()) {
    if (h.worldId !== worldId) continue;
    const id = u64(h.id);
    const owner = identityHex(h.owner);
    heroes.set(id, { owner, shapeMark: h.shapeMark, name: h.name, classId: CLASS_NAMES[h.class] ?? 'fighter', controller: grants.get(id) ?? owner });
  }
  const owners: Record<number, string> = {}; const heroNames: Record<number, string> = {}; const controlled: number[] = [];
  const meta = new Map<number, HeroMeta>();
  for (const m of members) {
    const id = u64(m.heroId);
    const h = heroes.get(id);
    const controller = h?.controller ?? identityHex(m.owner);
    owners[id] = controller;
    heroNames[id] = h?.name ?? `hero ${id}`;
    meta.set(id, { owner: controller, shapeMark: (h?.shapeMark ?? m.shapeMark) + 1, classId: h?.classId ?? 'fighter' });
    if (m.deployed && controller === me) controlled.push(id);
  }
  // docs/59 CC6: a deployed ranger's companion is planned by the ranger's controller
  const companions: number[] = [];
  for (const k of c.db.heroCalling.iter()) {
    if (k.worldId !== worldId || !isCompanionPick(k.pick)) continue;
    const ranger = u64(k.heroId);
    const m = members.find((x) => u64(x.heroId) === ranger);
    if (!m) continue;
    const id = companionIdOf(ranger);
    const controller = owners[ranger] ?? identityHex(m.owner);
    owners[id] = controller;
    heroNames[id] = k.petName || COMPANION_KINDS[k.pick] || 'Companion';
    meta.set(id, { owner: controller, shapeMark: meta.get(ranger)?.shapeMark ?? 1, classId: 'companion' });
    if (m.deployed && controller === me) companions.push(id);
  }
  // only the companions standing on this board (a fight begun before docs/59 has none)
  const withCompanions = (snap: Snapshot | undefined): number[] =>
    [...controlled, ...companions.filter((id) => snap?.units.some((u) => u.heroId === id))].sort((a, b) => a - b);
  controlled.sort((a, b) => a - b);
  bridge.owners = new Map(Object.entries(owners).map(([k, v]) => [Number(k), v]));
  // docs/184: the players' colours, the same as the World lobby's
  const world = [...c.db.world.iter()].find((w) => w.id === worldId);
  const colours = worldColours([...c.db.worldMember.iter()].filter((m) => m.worldId === worldId), [...c.db.playerColour.iter()].filter((p) => p.worldId === worldId), world ? identityHex(world.owner) : '');
  const prevColours = useEncounter.getState().colours;
  const coloursSame = prevColours.size === colours.size && [...colours].every(([k, v]) => prevColours.get(k) === v);

  // 2. intents in the cache (decoded once per row revision)
  currentIntents = [...c.db.intent.iter()].filter((r) => r.encounterId === encounterId).map((r): ServerIntent => {
    const key = `${r.id}:${r.revision}:${r.committed}:${r.substituted}`;
    let intent = decodeCache.get(key);
    if (!intent) { intent = bridge!.decodeIntent(r.intent); decodeCache.set(key, intent); }
    return { rowId: r.id, heroId: u64(r.heroId), slot: r.slot, pulseIndex: r.pulseIndex, snapshotHash: bytesToHex(r.snapshotHash), revision: r.revision, committed: r.committed, substituted: r.substituted, owner: identityHex(r.owner), intent };
  });
  if (decodeCache.size > 2000) decodeCache.clear();

  // 3. locks: parity over the intents as they stood before this transaction (11 §6.4). docs/54 RC4:
  // the first flush after a reconnect's replay meets pulses that locked while we were away, with
  // no record of the intents they locked on: those are caught up, not checked.
  const caughtUp = catchingUp && replayApplied;
  if (caughtUp) { catchingUp = false; replayApplied = false; }
  const pulses = [...c.db.pulse.iter()].filter((p) => p.encounterId === encounterId).sort((a, b) => a.index - b.index);
  const latest = pulses[pulses.length - 1];
  for (const p of pulses) {
    if (!p.locked || lockedSeen.has(p.index)) continue;
    lockedSeen.add(p.index);
    if (caughtUp) continue;
    const handleForPulse = useEncounter.getState().handle;
    const pulseHash = bytesToHex(p.snapshotHash);
    // docs/31 J8: under fog of war this client holds the party's view, not the truth the server
    // resolved, so neither hash can match; parity is for clear fields — and docs/41 PZ3: a puzzle
    // room's lock is shown without its answer, the same kind of view
    const view = handleForPulse ? bridge.snapshotFor(handleForPulse) : undefined;
    // — and docs/52 HT3: hidden things and disguised traps struck out of the view
    const fogged = !!(view?.fog || view?.partyView || view?.fixtures?.some((f) => f.kind === 'Lock'));
    if (handleForPulse && handleForPulse.hash === pulseHash && !fogged) {
      const rows = intentsBeforeTx.filter((i) => i.pulseIndex === p.index && i.snapshotHash === pulseHash);
      const client = bridge.planHash(handleForPulse, rows.map((i) => i.intent));
      const server = bytesToHex(p.planHash);
      useEncounter.setState({ lastPlanHash: { pulse: p.index, client, server }, lockedAt: deviceNow() });
      if (client !== server) {
        console.error('[parity] plan hash mismatch', { pulse: p.index, snapshotHash: pulseHash, intents: rows, client, server });
        useEncounter.setState((s) => ({ parity: [...s.parity, { kind: 'plan', pulse: p.index, expected: server, got: client }] }));
      }
      if (DEV_PARITY) {
        for (const pl of pendingLogs) {
          if (pl.row.pulseIndex !== p.index) continue;
          try {
            const mine = bridge.resolveForParity(handleForPulse, rows.map((i) => i.intent));
            const theirs = bridge.decodeLog(pl.row.log);
            if (mine.endSnapshotHash !== theirs.endSnapshotHash) {
              console.error('[parity] resolve mismatch', { pulse: p.index, mine: mine.endSnapshotHash, theirs: theirs.endSnapshotHash });
              useEncounter.setState((s) => ({ parity: [...s.parity, { kind: 'resolve', pulse: p.index, expected: theirs.endSnapshotHash, got: mine.endSnapshotHash }] }));
            }
          } catch (err) { console.error('[parity] resolve failed', err); }
        }
      }
    }
  }

  // 4. resolution logs → playback queue (played before the board swaps, 11 §9.2)
  if (pendingLogs.length) {
    for (const pl of pendingLogs) {
      try { logQueue.push(bridge.decodeLog(pl.row.log)); } catch (err) { console.error('[encounter] decode_log failed', err); }
    }
    pendingLogs = [];
  }

  // 5. the snapshot: load once per hash; authoring moves to it at once, the board after playback
  // a staged encounter (docs/22 O3) carries its kit, hour, weather and view seed beside the rules
  const stage = [...c.db.encounterStage.iter()].find((r) => r.encounterId === encounterId);
  // (docs/186 EA13–EA14: an outdoor place's country and the rooms' floors and rails, decoded once a snapshot)
  const encHash = `${bytesToHex(enc.snapshotHash)}|${stage ? `${stage.kit}:${stage.hour}:${stage.weather}:${stage.viewSeed}:${stage.season}:${stage.country ?? ''}:${lookKey(stage.look)}` : ''}`;
  const st = useEncounter.getState();
  let snapshotChanged = false;
  if (encHash !== loadedHash || !st.handle) {
    const handle = bridge.load(enc.snapshot, enc.declaration, meta, bytesToHex(enc.snapshotHash));
    const loaded = bridge.snapshotFor(handle)!;
    // docs/23: every fight in a World with a clock is staged — an empty kit keeps the room's own
    // (indoors), the season dresses the board
    const season = seasonKey(stage?.season);
    const look = stage ? decodeLook(stage.look, loaded.board.w, loaded.board.h) : undefined;
    const snapshot: Snapshot = stage ? { ...loaded, board: { ...loaded.board, kit: stage.kit || loaded.board.kit, hour: stage.hour, weather: stage.weather, seed: stage.viewSeed, ...(season ? { season } : {}), ...(stage.country ? { country: stage.country } : {}), ...(look ? { look } : {}) } } : loaded;
    if (prevHandle && prevHandle.id !== st.handle?.id) bridge.free(prevHandle);
    prevHandle = st.handle;
    loadedHash = encHash;
    snapshotChanged = true;
    const showNow = !usePlayback.getState().playing && logQueue.length === 0 && !waitingForPlayback;
    useEncounter.setState({ handle, snapshot, snapshotHashBytes: enc.snapshotHash, pulseIndex: enc.pulseIndex, ...(showNow || !st.boardSnapshot ? { boardSnapshot: snapshot } : {}) });
    useAuthoring.getState().attach(bridge, handle, snapshot, me, withCompanions(snapshot), encounterId, enc.snapshotHash);
  }
  const mine = withCompanions(useEncounter.getState().snapshot);
  useEncounter.setState({ owners, heroNames, ...(coloursSame ? {} : { colours }), controlled: mine, pulseIndex: enc.pulseIndex, state: enc.state, outcome: enc.outcome ?? undefined, ...(latest && !latest.locked ? { lockedAt: undefined } : {}) });
  if (!snapshotChanged) useAuthoring.getState().setControlled(mine);
  useAuthoring.getState().serverChanged(currentIntents);
  // docs/149 MP: the walks the server holds, each as the hero and the square it is going to
  useAuthoring.getState().serverRoutes([...c.db.heroRoute.iter()].filter((r) => r.encounterId === encounterId).map((r) => ({ heroId: u64(r.heroId), x: r.destX, y: r.destY })));

  // 6. play the next log if the scene is free
  pumpPlayback();
  // 7. docs/54 RC5: plans placed while offline go out now the board is current
  if (caughtUp) resendUnsent();
}

function pumpPlayback(): void {
  const pb = usePlayback.getState();
  if (pb.playing || waitingForPlayback) return;
  const next = logQueue.shift();
  if (!next) return;
  const encounterId = current ? Number(current.encounterId) : next.encounterId;   // the row's id; the log's is the sim's seed id
  countStreak(next);
  if (!pb.hasPlayer) { playAfterPlayback(encounterId, next.pulseIndex); afterPlayback(); return; }    // no scene mounted: catch the board up at once (docs/34: entry cues skip, what follows plays)
  waitingForPlayback = true;
  // docs/47: the board still shows the pulse's start, where its spells fly from
  pb.play(next, encounterId, useEncounter.getState().boardSnapshot);
}

/** docs/101 KS1: my account announces my kill streaks (`player_setting.kill_streaks`). */
function killStreaksOn(): boolean {
  const c = getConn();
  return !!c && [...c.db.myPlayerSetting.iter()].some((r) => r.killStreaks);
}

/** docs/101 KS2: the streak moves along every log, played or caught up past; the scene calls the kills it plays. */
function countStreak(log: ResolutionLog): void {
  if (!killStreaksOn()) return;
  const s = useEncounter.getState();
  const kills = countLog(log, streakBoard([s.boardSnapshot, s.snapshot], s.owners, s.me));
  // turned on mid-fight: the lines are fetched as the first kills come
  if (kills.length) void preloadSfx(kills.map((k) => k.call.sfx), true);
}

function afterPlayback(): void {
  const s = useEncounter.getState();
  if (s.snapshot && s.boardSnapshot !== s.snapshot && logQueue.length === 0) useEncounter.setState({ boardSnapshot: s.snapshot });
  if (logQueue.length) queueMicrotask(pumpPlayback);
  else if (s.busy && !pendingLogs.length) useEncounter.setState({ busy: false });
}

/**
 * docs/56: the handles of the board a playback starts on and the one after it, while the pulse
 * being played is the last one in (with more queued, the board after is not this pulse's).
 */
export function playbackHandles(): { start: SnapshotHandle; end: SnapshotHandle } | undefined {
  const s = useEncounter.getState();
  if (!bridge || !prevHandle || !s.handle || logQueue.length || !s.boardSnapshot) return undefined;
  if (bridge.snapshotFor(prevHandle)?.snapshotHash !== s.boardSnapshot.snapshotHash) return undefined;
  return { start: prevHandle, end: s.handle };
}

// ---- helpers for the HUD -------------------------------------------------------------------------

/**
 * The board the HUD shows: as it stands part-way through a playback, else the one the scene
 * draws — never the board after a pulse that is still being played out (docs/56).
 */
export function useShownSnapshot(): Snapshot | undefined {
  const live = usePlayback((p) => p.live);
  const board = useEncounter((s) => s.boardSnapshot ?? s.snapshot);
  return live ?? board;
}

export const pulseDeadlineMs = (p: { deadlineAt?: { microsSinceUnixEpoch: bigint } | null | undefined } | undefined): number | undefined => tsToMs(p?.deadlineAt as never);
